import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Container,
  H3,
  Flex,
  Spinner,
  FlexItem,
  styled,
} from '@erecruit/ui-kit';
import { connect } from 'react-redux';
import { Intl, useDebounce } from '@erecruit/core';
import { DataLevel, Resources } from '@erecruit/types';
import {
  SettingsRepository,
  saveDataPreferences,
  fetchAllCompanies,
  initializeSettings,
} from '@erecruit/modules';
import { RootState } from '../../../../redux/reducers';
import { CompaniesPicker } from './components/CompaniesPicker';
import { PositionTypePicker } from './components/PositionTypePicker';
import { DataPreferencesCategoryPicker } from './components/DataPreferencesCategoryPicker';

const { FormattedMessage, injectIntl } = Intl;

const DataPreferencesContainer = styled(Container)({
  width: '70%',
  minWitdth: 400,
});

const DataPreferences = injectIntl(
  ({ contactId, intl }: { contactId: number } & Intl.InjectedIntlProps) => {
    const dataPrefsRefreshed = useRef(false);
    const [companies, setCompanies] = useState<Resources.Company[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [dataSettings, setDataSettings] = useState(
      SettingsRepository.getInstance().settings.dataPreferences,
    );

    useEffect(() => {
      const fetch = async () => {
        try {
          setIsLoading(true);
          await initializeSettings(contactId);
          const companies = await fetchAllCompanies();
          setCompanies(companies);
          setDataSettings(
            SettingsRepository.getInstance().settings.dataPreferences,
          );
          dataPrefsRefreshed.current = true;
          setIsLoading(false);
        } catch (e) {
          setIsLoading(false);
        }
      };

      fetch();
    }, [contactId]);

    const onPositionTypePickerChange = useCallback(
      (selectedPositionTypes: number[]) => {
        setDataSettings(settings => ({
          ...settings,
          positionTypes: selectedPositionTypes,
        }));
      },
      [setDataSettings],
    );

    const onCompanyDepartmentsChange = useCallback(
      (selectedDepartments: Record<number, number[]>) => {
        setDataSettings(settings => {
          const newSelectedDepartmentsForCompany = Object.keys(
            selectedDepartments,
          ).map(x => ({
            companyId: Number(x),
            departmentIds: selectedDepartments[Number(x)],
          }))[0];

          return {
            ...settings,
            companies: [
              ...settings.companies.filter(
                company =>
                  company.companyId !==
                  newSelectedDepartmentsForCompany.companyId,
              ),
              newSelectedDepartmentsForCompany,
            ],
          };
        });
      },
      [setDataSettings],
    );

    const debouncedSettings = useDebounce(dataSettings, 1000);

    useEffect(() => {
      if (dataPrefsRefreshed) {
        SettingsRepository.getInstance().set(
          'dataPreferences',
          debouncedSettings,
        );
        saveDataPreferences();
      }
    }, [debouncedSettings, dataPrefsRefreshed]);

    return isLoading ? (
      <Flex>
        <Spinner
          size="large"
          tip={intl.formatMessage({
            id: messageIds.vms.settings.data.initializing,
          })}
        />
      </Flex>
    ) : (
      <DataPreferencesContainer background="pageSecondary">
        <Container background="pageSecondary" padding={[0, 6]}>
          <DataPreferencesCategoryPicker
            selectedCategory={dataSettings.option}
            onSettingsChange={option =>
              setDataSettings(settings => ({ ...settings, option }))
            }
          />
        </Container>
        <Flex
          padding={[10, 6]}
          direction="column"
          gutter={6}
          gutterDirection="vertical"
        >
          <FlexItem gutter={2} gutterDirection="vertical">
            <H3 weight={400}>
              <FormattedMessage
                id={messageIds.vms.settings.data.positionTypeFilterTitle}
              />
            </H3>
            <PositionTypePicker
              onChange={onPositionTypePickerChange}
              defaultSelection={dataSettings.positionTypes}
            />
          </FlexItem>
          {dataSettings.option === DataLevel.MyCompany && (
            <CompaniesPicker
              dataSettings={dataSettings}
              companies={companies}
              onChange={onCompanyDepartmentsChange}
            />
          )}
        </Flex>
      </DataPreferencesContainer>
    );
  },
);

export default connect((state: RootState) => ({
  contactId: Number(state.oidc.user?.profile.erecruit_logged_in_id),
}))(DataPreferences);
